import React, { useLayoutEffect, useRef, useState } from "react";

import { visibleDashboardTimeRangeOptions } from "../../../services/helpers/DashboardHelpers";

interface IProps {
  timeRange: string;
  onTimeRangeChange: (value: string) => void;
  showLabel?: boolean;
  unavailableTimeRanges?: string[] | null;
  className?: string;
}

export const DashboardTimeRangePills = (props: IProps) => {
  const options = visibleDashboardTimeRangeOptions(props.unavailableTimeRanges);
  const optionsKey = options.map((range) => range.value).join(",");

  // Swap the pill row for a dropdown when the space we're given is narrower than the pills need.
  // The pills' natural width is captured while they're rendered, then reused while collapsed.
  const containerRef = useRef<HTMLDivElement>(null);
  const pillsRef = useRef<HTMLDivElement>(null);
  const pillsWidthRef = useRef(0);
  const [collapsed, setCollapsed] = useState(false);

  // Options changed: re-expand so the new pill row gets measured.
  useLayoutEffect(() => {
    pillsWidthRef.current = 0;
    setCollapsed(false);
  }, [optionsKey]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;

    const update = () => {
      if (pillsRef.current) pillsWidthRef.current = pillsRef.current.offsetWidth;
      if (pillsWidthRef.current === 0) return;
      setCollapsed(container.clientWidth < pillsWidthRef.current);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(container);
    return () => observer.disconnect();
  }, [collapsed, optionsKey]);

  return (
    <div className={`flex min-w-0 items-center gap-2 ${props.className ?? ""}`}>
      {props.showLabel ? (
        <span className="text-sm text-slate-600">Range</span>
      ) : null}
      <div ref={containerRef} className="flex min-w-0 flex-1 justify-end">
        {collapsed ? (
          <select
            aria-label="Time range"
            className="cursor-pointer rounded-full bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-900 outline-none"
            value={props.timeRange}
            onChange={(event) => props.onTimeRangeChange(event.target.value)}
          >
            {options.map((range) => (
              <option key={range.value} value={range.value}>
                {range.label}
              </option>
            ))}
          </select>
        ) : (
          <div ref={pillsRef} className="flex w-max shrink-0 rounded-full bg-slate-100 p-1">
            {options.map((range) => (
              <button
                key={range.value}
                className={`rounded-full px-3 py-1 text-sm font-medium transition ${
                  props.timeRange === range.value
                    ? "bg-white text-slate-900 shadow"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                onClick={() => props.onTimeRangeChange(range.value)}
                type="button"
              >
                {range.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
